import React from 'react'
import { useController, useFormContext } from 'react-hook-form'
import styled from 'styled-components'
import { DSIcon } from '@zoe/ds-web'

const RadioWrapper = styled.div`
  flex: 1;
  margin-bottom: var(--grid-16);
  /* Contains HiddenInput. Without a positioned ancestor it resolves against the
     initial containing block, escaping ScreenShell's scroll region and adding
     its own height to the document (WEB-895). */
  position: relative;
`

const HiddenInput = styled.input`
  opacity: 0;
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
`

const RadioLabel = styled.label<{ $isSelected: boolean; $showArrow?: boolean }>`
  display: ${({ $showArrow }) => ($showArrow ? 'flex' : 'block')};
  align-items: center;
  justify-content: space-between;
  gap: var(--grid-12);
  padding: var(--grid-16);
  border: 2px solid
    ${({ $isSelected }) => ($isSelected ? 'var(--colour-text-primary)' : 'var(--colour-surface-cream-subtle)')};
  border-radius: var(--radius-12);
  background-color: var(--colour-surface-cream-subtle);
  cursor: pointer;
  transition: border-color 0.15s ease;

  /* Only apply hover styling on devices that actually have a pointing
     device — on touch screens the hover state sticks after a tap and
     makes the just-tapped option look like it's still highlighted. */
  @media (hover: hover) {
    &:hover {
      border-color: var(--colour-text-secondary);
    }
  }

  &:has(input:focus-visible) {
    border-color: var(--colour-border-focused);
    outline: 2px solid var(--colour-border-focused);
    outline-offset: 2px;
  }
`

export interface RadioButtonProps extends React.ComponentProps<'input'> {
  label?: React.ReactNode
  name: string
  clicked?: (value: any) => void
  showArrow?: boolean
}

export const RadioButton: React.FC<RadioButtonProps> = ({ clicked, showArrow, ...props }) => {
  const { field } = useController({ name: props.name })
  const { setValue } = useFormContext()

  const handleChange = async () => {
    setValue(props.name, props.value, { shouldDirty: true, shouldValidate: true })

    if (clicked) clicked(props.value)
  }

  const isSelected = field.value === props.value

  return (
    <RadioWrapper className={props.className ?? ''}>
      <HiddenInput
        type="radio"
        id={props.id || `${props.name}-${props.value}`}
        name={field.name}
        ref={field.ref}
        onBlur={field.onBlur}
        value={props.value}
        checked={isSelected}
        onChange={handleChange}
        // Fire on click too so re-selecting an already-checked radio still
        // triggers the handler (native onChange skips when the value is
        // unchanged). Guarded on isSelected to avoid double-firing on
        // changes (where both onClick and onChange fire).
        onClick={() => isSelected && handleChange()}
        data-testid={`${props.name}-${props.value}-btn`}
      />

      <RadioLabel
        $isSelected={isSelected}
        $showArrow={showArrow}
        htmlFor={props.id || `${props.name}-${props.value}`}
        data-testid={`${props.name}-${props.value}-label`}
      >
        <span>{props.label || props.value}</span>
        {showArrow && <DSIcon name="chevron-right" size={16} />}
      </RadioLabel>
    </RadioWrapper>
  )
}
